// Copyright (c) 2025 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { IParameter, Bus, fromBusNr, getBusNr, IAddress } from "./parameter";

/*
  This module defines all types shared accross cloud components.
  Typically such types are used in database, and in frontend and/or lambdas.
*/

import { AlarmSource } from "shared/types/globtypes";

/*
 * DB constant definitions
 */

// epex id of t_tariff_variable, not of t_tariff!
export const EPEX_TARIFF_ID = 1;

// define maximum number of data-entries for requesting ALL trend-data
// after aggregated-data was queried...
export const IO_TREND_LIMIT = 20000;

export const COMPANY_EMBYT_ID = 1;

/*
 * DB table definitions
 */

export interface AlarmSettings {
  offlineTimeout?: number; // s, until site offline alert triggers; zero to disable
  chargerOfflineTimeout?: number; // s, until ocpp charger offline alert triggers; zero to disable
  chargerFaultedTimeout?: number; // s, until ocpp charger connector failure alert triggers; zero to disable

  // charging interrupted alarm: at least one of its two triggers must be enabled to activate alarm
  chargingPausedTimeout?: number; // s, pause duration until the alarm triggers; zero to disable
  chargingStoppedAlarm?: boolean; // alarm when a charging session stopped
  // define the SoC limits, which enable their trigger as well
  chargingInterruptedSoc?: number; // %, SoC limit of the paused trigger; unset alarms at any SoC
  chargingEndedSoc?: number; // %, SoC limit of the stopped trigger; unset alarms at any SoC
  // charging interrupted filters
  chargingInterruptedChargers?: number[]; // t_charger ids the alarm is limited to; empty: all
  chargingInterruptedRfids?: number[]; // t_rfid ids the alarm is limited to; empty: all
}

// interval of alarm notification emails, see t_alarm_notification.period
export enum NotificationPeriod {
  MINUTE = "minute",
  HOUR = "hour",
  DAY = "day",
}

// one alarm notification email per period, listing all alarms of that period, see t_alarm_notification
export interface AlarmNotification {
  id: number;
  userId: number;
  name?: string;
  isActive: boolean;
  period: NotificationPeriod;
  sources: AlarmSource[]; // always lists all selected sources
  recipients?: string; // comma separated; undefined sends to the owning user email
  includeCleared: boolean;
  allSites: boolean; // notify on all sites of the user, ignoring siteIds
  siteIds: number[];
}

export interface ReportsSettings {
  endCustomer?: string;
  orderNumber?: string;
  serviceType?: string;
  annotation?: string; // comment
  address?: string; // site address, used in charger reports
}

/*
  this is the specification of data retention policies:
  see ticket #973, but the timings were adapted later
  DB-Model  Sessionliste 	Ladeverlauf 	Verbrauchsdaten 	    IO-Trending 	Alarme
  small 	  6 Monate 	    1 Monate 	    6 Monate 	            1 Monate 	    1 Monate
  large	    3 Jahre 	    6 Monate      1 Jahr              	6 Monate 	    6 Monate
  unlim 	  unlimitiert 	1 Jahr   	    2 Jahre               1 Jahr 	      1 Jahr
  For Verbrauchsdaten, after the retention period, daily aggregated data is kept unlimited.
*/
export enum DeletionStrategy {
  SMALL = "small",
  LARGE = "large",
  UNLIMITED = "unlimited",
}

export interface CloudSettings {
  alarm?: AlarmSettings;
  reportsSettings?: ReportsSettings;
  trendMonitor?: TrendMonitor[];
  deletionStrategy?: DeletionStrategy;
  customDashboard?: any; // see CustomDashboard interface in parameter.ts
}

export interface UserSettings {
  showChargerTariff?: boolean;
}

export interface UserAccessRights {
  setVirtualInputs?: boolean;
  switchOutputs?: boolean;
  setParameter?: boolean;
  ackAlarms?: boolean;
  ocppSettings?: boolean; // RFIDs, tariffs, chargers public/private
  ocppChargerAdmin?: boolean; // delete charger, change connector names
  ocppChargerCtrl?: boolean; // start/stop transactions, unlock connector, reboot charger
  createReports?: boolean; // can create reports
  crudTrendMonitors?: boolean; // create/read/update/delete trend-monitors
  modifyAlarms?: boolean; // activate/modify alarms
  importTrendData?: boolean; // allowed to upload trend files
  showEnlineChargers?: boolean; // show energy management chargers (independent of ocpp chargers)
  controllerAdmin?: boolean; // show extended controller data (modules, conditions) and allow reboot
  openSshTunnel?: number; // relay port for ssh tunnels to site controllers, unset: no access
  setVirtualParameters?: boolean; // json parameter entries for virtual sites
  siteAccess?: boolean; // can recursively use all sites belonging to company and subsidiaries
  modifyUsers?: boolean; // can recursively use all users belonging to company and subsidiaries
  modifySites?: boolean; // can recursively change all sites belonging to company and subsidiaries
}

export interface RfidAccessRights {
  isLoginAllowed?: boolean;
  isChargerPowerCtrlAllowed?: boolean; // set charging power limits and strategy
}

// keep consistent with ModalRfidSettings.tsx
export enum eRfidLimitPeriod {
  DAY = "DAY",
  MONTH = "MONTH",
  YEAR = "YEAR",
}

export enum FileUploadStatus {
  INITIAL = "initial",
  RECEIVED = "received",
  PROCESSED = "processed",
  ERROR = "error",
}

// definitions for data reports
// the order corresponds to the report column order
export enum TrendingColum {
  TARIFF_ENERGY = "tariff_energy",
  ENERGY = "energy",
  ENERGY_MIN = "energy_min",
  ENERGY_AVG = "energy_avg",
  ENERGY_MAX = "energy_max",
  MAX_ENERGY = "max_energy",
  TARGET_VALUE = "target_value",
  MAX_POWER = "max_power",
  TARIFF_COST = "tariff_cost",
  COST = "cost",
  PEAK_COST = "peak_cost",
  ENERGYMETER = "energymeter",
}

export enum Period {
  HOUR = "hour",
  DAY = "day",
  MONTH = "month",
  YEAR = "year",
}
export enum ExtendedPeriod {
  LAST_DAY = "last_day",
  LAST_MONTH = "last_month",
  LAST_YEAR = "last_year",
}

export interface ICloudReportColumnDefinition {
  title: string;
  value: string;
  unit?: string;
  decimals?: number;
  aggregation?: "SUM" | "MAX" | "AVERAGE";
}

interface ReportContent {
  customColumn?: ICloudReportColumnDefinition[];
}

export interface IReportFilter {
  type: string;
  ref?: number | Period | ExtendedPeriod; // for reference filters; Period for period, else number
}

export interface ITrendReportFilter extends IReportFilter {
  type: "period" | "begin" | "end" | "circuit" | "input" | "output";
}

export type TrendReportContent = ReportContent & {
  groupBy: ("date" | "circuit")[];
  // total: a single sum over the whole period, without date column
  aggregation: Period | "none" | "total";
  columns: TrendingColum[];
  filter: ITrendReportFilter[];
  reference:
    | {
        type: "date" | "circuit";
        ref: number | Period; // Period for period, number for circuit
      }
    | undefined;
  hideEmpty: boolean;
  isEvuFormat?: boolean;
};

// deprecated: replaced by Report.exportInterval and Report.recipients
export interface AutoExport {
  target: string;
  interval: Period | "none";
}

// the order corresponds to the report column order
export enum ChargerColum {
  STOP_TIME = "stop_time",
  ENERGY = "energy",
  DURATION = "duration",
  RFID = "rfid",
  USER = "user",
  VEHICLE = "vehicle",
  SITE = "site",
  ADDRESS = "address",
  CHARGER = "charger",
  AC_DC = "ac_dc",
  CONNECTOR = "connector",
  EVSEID = "evseid",
  METER_VALUE = "meter_value",
  STOP_METER_VALUE = "stop_meter_value",
  PLUG_TYPE = "plug_type",
  MAX_POWER = "max_power",
  PHASES = "phases",
  COORDINATES = "coordinates", // longitude and latitude
  COSTS = "costs",
  TARIFF = "tariff",
  STOP_REASON = "stop_reason",
  CATEGORY = "category",
  ADDITIONAL_DATA = "additional_data",
}

export interface IChargerReportFilter extends IReportFilter {
  type: "period" | "begin" | "end" | "category" | "rfiduser" | "charger";
  value?: string; // for category or rfiduser filter
  // for charger filter, ref holds the charger id (t_charger.id)
}

export type ChargerReportContent = ReportContent & {
  columns: ChargerColum[];
  filter: IChargerReportFilter[];
};

export interface IIoReportFilter extends IReportFilter {
  type: "period" | "begin" | "end" | "input" | "output";
}

export type IoReportContent = ReportContent & {
  aggregation: Period | "none";
  filter: IIoReportFilter[];
};

// the order corresponds to the report column order
export enum SiteColum {
  SITE_INFO = "site_info",
  // company, end customer, order number, service type, db model
  COMPANY_INFO = "company_info",
  // added on, last access, access count, users
  USAGE_INFO = "usage_info",
  // device type, serial number, software, sw branch
  DEVICE_INFO = "device_info",
  // enline chargers, ocpp chargers, ocpp connectors
  CHARGER_COUNTS = "charger_counts",
  // regulations, tariff lines, energy meters, outputs, inputs, conditions, modules, reports, trend monitors
  PARAMETER_COUNTS = "parameter_counts",
  // cloud alarm timeout, charger alarm timeout
  ALARM_TIMEOUTS = "alarm_timeouts",
  ANNOTATIONS = "annotations",
}

export type SiteReportContent = ReportContent & {
  // deprecated: the company scope moved to Report.companyId
  companyId?: number;
  includeSubcompanies?: boolean;
  columns?: SiteColum[]; // if empty/undefined, all columns are shown (for compatibility with old reports)
  filter: IReportFilter[]; // currently unused
};

// the order corresponds to the report column order
// the rfid card column is always shown
export enum RfidColum {
  NAME = "name",
  CATEGORY = "category",
  ADDITIONAL_DATA = "additional_data",
  ADDED_ON = "added_on",
  LAST_USAGE = "last_usage",
  USAGE_COUNT = "usage_count",
  CHARGER_RESTRICTION = "charger_restriction", // number of chargers the card is limited to
  LIMIT_START = "limit_start",
  LIMIT_END = "limit_end",
  LIMIT_ENERGY = "limit_energy", // energy limit and its period
  LIMIT_COST = "limit_cost", // cost limit and its period
  MAX_POWER = "max_power",
  LOGIN_ALLOWED = "login_allowed",
  CHARGING_CONTROL = "charging_control", // charger power control allowed
  VEHICLE = "vehicle",
  BATTERY = "battery",
  LINKED_SITES = "linked_sites", // number of additionally linked sites
  IS_ACTIVE = "is_active",
}

export interface IRfidReportFilter extends IReportFilter {
  type: "category";
  value?: string;
}

export type RfidReportContent = ReportContent & {
  columns: RfidColum[];
  filter: IRfidReportFilter[];
};

// a data report, owned by a user and scoped to one site or, for site reports,
// to one company, see t_report
export interface Report {
  id: number;
  userId: number;
  siteId?: number; // data scope of trend, charger, io and rfid reports
  companyId?: number; // data scope of site reports
  title: string;
  isActive: boolean;
  type: "trend" | "io" | "charger" | "rfid" | "site";
  content:
    | TrendReportContent
    | IoReportContent
    | ChargerReportContent
    | RfidReportContent
    | SiteReportContent;
  exportInterval: Period | "none"; // interval of the automatic report export
  recipients?: string; // comma separated; undefined sends to the owning user email
  // ensure to have this consistent with sheet-js supported output format (BookType)
  fileFormat?: "xlsx" | "xls" | "ods" | "csv" | "txt" | "html" | "rtf";
  csvDelimiter?: ";" | "," | "\t" | "|";
  hideSumRow?: boolean;
  // deprecated: replaced by exportInterval and recipients, only kept until the migration
  // of the site cloud settings to t_report is completed
  autoExport?: AutoExport;
}

export interface TrendMonitor {
  title: string;
  isActive: boolean;
  circuit: number;
  period: Period;
  targetValue?: number; // fixed number, not used for reference periods
  referencePeriod?: Period; // the relative distance for the reference, not used for fixed target values
  tolerance: number; // the overshoot tolerance for the alarm trigger
  slope: number; // similar to "Abschaltgerade" of regulation, the slope of the alarm trigger from end of period
}

export enum ChargingMode {
  CostOptimized = "costOptimized",
  Early = "early",
  Late = "late",
  PVOptimized = "pvOptimized",
  Uniform = "uniform",
}

export interface IUserChargingControl {
  isActive: boolean;
  useAtNewSession: boolean;
  mode: ChargingMode;
  maxPower: number | undefined;
  targetEnergy: number | undefined;
  targetTimestamp: number | undefined;
}

/* defines references in a virtual parameter assignement */
// also refer to parameter.ts, which describes the resolved definition in the API/parameter set
export interface IVirtSiteDefinition {
  input?: IVirtDefinition[];
  energymeter?: IVirtDefinition[];
  tariffline?: IVirtDefinition[];
  regulation?: IVirtDefinition[];
  output?: IVirtDefinition[];
  charger?: IVirtDefinition[];
  ocpp?: IVirtDefinition[];
}

export interface IVirtDefinition {
  sourceSite: number;
  // either sourceIndex, sourceId or sourceName must be set
  sourceIndex?: number; // array index of the source
  sourceId?: number; // id of the source
  sourceName?: string; // regular expression to match source name; not allowed for ocpp chargers
  // possibilities to overwrite source parameter, e.g. its name
  name?: string;
  isEnabled?: boolean;
  webActive?: boolean;
  webCtrl?: boolean;
}

export interface IVirtSiteAssign {
  input?: IVirtAssign[];
  energymeter?: IVirtAssign[];
  tariffline?: IVirtAssign[];
  regulation?: IVirtAssign[];
  output?: IVirtAssign[];
  charger?: IVirtAssign[];
  ocpp?: IVirtAssign[];
}

export interface IVirtAssign {
  sourceSite: number;
  sourceIndex: number; // the resolved array index of the source parameter set
  sourceId?: number; // the resolved id (index if it has no id), undefined for ocpp
  destIndex?: number; // the resolved array index in the destination parameter set; only undefined for ocpp
  // possibilities to overwrite source parameter, e.g. its name
  name?: string;
  isEnabled?: boolean;
  webActive?: boolean;
  webCtrl?: boolean;
  // used to add custom columns to reports
  reportColumn?: ICloudReportColumnDefinition[];
}

// extended parameters, as used in the cloud
export type ICloudParameter = IParameter & {
  virtualSite: IVirtSiteAssign;
};

export enum ChargerStatusError {
  // ocpp codes
  CONNECTOR_LOCK_FAILURE = "ConnectorLockFailure",
  EV_COMMUNICATION_ERROR = "EVCommunicationError",
  GROUND_FAILURE = "GroundFailure",
  HIGH_TEMPERATURE = "HighTemperature",
  INTERNAL_ERROR = "InternalError",
  LOCAL_LIST_CONFLICT = "LocalListConflict",
  NO_ERROR = "NoError",
  OTHER_ERROR = "OtherError",
  OVER_CURRENT_FAILURE = "OverCurrentFailure",
  POWER_METER_FAILURE = "PowerMeterFailure",
  POWER_SWITCH_FAILURE = "PowerSwitchFailure",
  READER_FAILURE = "ReaderFailure",
  RESET_FAILURE = "ResetFailure",
  UNDER_VOLTAGE = "UnderVoltage",
  OVER_VOLTAGE = "OverVoltage",
  WEAK_SIGNAL = "WeakSignal",
  //backend codes
  CHARGER_COMMUNICATION_ERROR = "ChargerCommunicationError",
  BOOT_NOTIFICATION = "BootNotification",
}

// connection type NULL indicates direct ocpp connection
export enum ChargerConnectionType {
  CLOUD = "cloud",
  PROXY = "proxy",
  PROXY_SECONDARY = "proxy-secondary",
  GATEWAY = "gateway",
  GATEWAY_SECONDARY = "gateway-secondary",
}

export interface IDataSubscription {
  subChargerStatusData?: boolean | null; // subscribes to charging session and charger status notification data
  subChargerPowerData?: boolean | null; // subscribes to dynamic charger data like current power, voltage,...
  subControllerPowerData?: boolean | null; // subscribes to input/output/energy data of energy management controller
  subControllerChargerData?: boolean | null; // deprecated: subscribes to charger data routed via energy management controller
}

export interface IFileImportResultTariffArgs {
  energyMeter: string;
  tariffs: number[];
  importedEntries: number;
}

export interface IFileImportResultArg {
  status: FileUploadStatus;
  message?: string;
  notFoundEMs?: string[];
  succeededEMs?: IFileImportResultTariffArgs[];
  multipleEMsFound?: string[];
  noTariffsFound?: string[];
  duration?: number;
}

export interface IDbAddress {
  bus: number;
  module: number;
  channel: number;
}

/***************************************************
 * Utility functions for type/parameter conversions
 ***************************************************/
// unfortunately, addresses in switching DB and in parameter set are different!
// we need to translate this
// also see switchinglistmodel.cpp::loadData()
export function dbAddr2ParaAddr(dbItem: IDbAddress, isOutput: boolean): IAddress {
  const bus = fromBusNr(dbItem.bus);
  // for Loytec devices:
  // in parameter set, module numbering starts at 1, except for virtual, module and charger busses
  // in IO DB, module numbering starts at 0, for all busses
  // for embyt devices: no renumbering at all, as we only have virtual/module/charger bus here
  const moduleOffset =
    (
      (isOutput ? [Bus.LC, Bus.FT, Bus.MOD, Bus.M, Bus.IP, Bus.LOCAL] : [Bus.MOD, Bus.M]).includes(
        bus,
      )
    ) ?
      1
    : 0;

  // see address.cpp::getMinOutput()
  // we removed the offset from Bus.Virtual as it seems wrong?
  const channelOffset =
    isOutput && [Bus.LC, Bus.LOCAL].includes(bus) ? 1
    : [Bus.FT, Bus.IP].includes(bus) ? 2
    : 0;

  return {
    bus,
    module: dbItem.module + moduleOffset,
    channel: dbItem.channel + channelOffset,
  };
}
export function paraAddr2DbAddr(paraItem: IAddress, isOutput: boolean): IDbAddress {
  // see dbAddr2ParaAddr
  const moduleOffset =
    (
      (isOutput ? [Bus.LC, Bus.FT, Bus.MOD, Bus.M, Bus.IP, Bus.LOCAL] : [Bus.MOD, Bus.M]).includes(
        paraItem.bus,
      )
    ) ?
      1
    : 0;
  const channelOffset =
    isOutput && [Bus.LC, Bus.LOCAL].includes(paraItem.bus) ? 1
    : [Bus.FT, Bus.IP].includes(paraItem.bus) ? 2
    : 0;
  return {
    bus: getBusNr(paraItem.bus),
    module: paraItem.module - moduleOffset,
    channel: paraItem.channel - channelOffset,
  };
}

// calculate io address value from bus, module and channel; bit 31 is 1 for input and 0 for output
export function prepareIOBusModuleChannel(
  isInput: boolean,
  bus: number,
  module: number,
  channel: number,
) {
  const value =
    (((isInput === true ? 1 : 0) << 31) & 0x80000000) |
    ((bus << 24) & 0x7f000000) |
    ((module << 8) & 0xffff00) |
    (channel & 0xff);

  return value;
}

export function extractIOBusModuleChannel(inValue: number): {
  isInput: boolean;
  bus: number;
  module: number;
  channel: number;
} {
  const isInput = inValue >> 31 !== 0;
  const bus = (inValue >> 24) & 0x7f;
  const module = (inValue >> 8) & 0xffff;
  const channel = inValue & 0xff;

  return { isInput, bus, module, channel };
}

export function makeVirtualSiteId(siteId: number, itemId: number) {
  return siteId * 10000 + itemId;
}
export function parseVirtualSiteId(virtSiteId: number) {
  return {
    siteId: Math.floor(virtSiteId / 10000),
    itemId: virtSiteId % 10000,
  };
}
